import * as React from 'react';
import styled from 'styled-components';
import { Keyframes, animated } from 'react-spring/renderprops.cjs';

const ListSpring: any = Keyframes.Spring({
  open: {
    delay: 0,
    height: 'auto',
    config: { tension: 220, friction: 25, clamp: true },
  },
  close: {
    delay: 0,
    height: 0,
    config: { tension: 220, friction: 25, clamp: true },
  },
});

const ArrowSpring: any = Keyframes.Spring({
  up: {
    transform: 'rotate(-180deg)',
    transformOrigin: 'center',
    width: 20,
    height: 20,
  },
  down: {
    transform: 'rotate(0deg)',
    transformOrigin: 'center',
    width: 20,
    height: 20,
  },
});

type Props = {
  header: string;
  dark?: boolean;
};

const PanelRoot = styled.div<{ dark: boolean }>(({ theme, dark }) => ({
  maxWidth: '816px',
  width: '100%',
  overflowY: 'hidden',

  '& > *': {
    backgroundColor: dark ? theme.blue15 : theme.blue14,
  },
}));

const PanelHeader = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  padding: '16px',
  height: '56px',

  '& svg': {
    width: '20px',
    height: '20px',
  },

  '@media screen and (min-width: 768px)': {
    height: '64px',
  },
});

const Header = styled.h3(({ theme }) => ({
  color: theme.white,
  fontSize: '16px',
  letterSpacing: '.17px',
  lineHeight: '20px',
  fontWeight: 400,
  margin: 0,
  userSelect: 'none',
}));

const PanelBody = styled(animated.div)(({ theme }) => ({
  marginTop: '1px',
  width: '100%',
  color: 'rgba(255,255,255,.9)',
  fontSize: '16px',
  letterSpacing: '.17px',
  lineHeight: '20px',
  fontWeight: 400,
  transition: `text-decoration ${theme.transitionEasing}`,
  userSelect: 'none',

  '& > div': {
    padding: '16px 16px 40px',
  },

  '& a': {
    color: theme.blue07,
    textDecoration: 'none',
    cursor: 'pointer',
  },

  '& a:hover': {
    textDecoration: 'underline',
  },
}));

const ExpansionPanel: React.FC<Props> = ({
  header,
  dark = false,
  children,
}) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <PanelRoot dark={dark}>
      <PanelHeader onClick={() => setExpanded((current) => !current)}>
        <Header>{header}</Header>
        <ArrowSpring native state={expanded ? 'up' : 'down'}>
          {(styles: any): React.ReactNode => (
            <animated.div style={styles}>
              <svg width="32px" height="32px" viewBox="0 0 32 32">
                <g
                  id="Icon/down"
                  stroke="none"
                  strokeWidth="1"
                  fill="none"
                  fillRule="evenodd"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline
                    id="Path"
                    stroke="#fff"
                    strokeWidth="2.2"
                    transform="translate(16.000000, 16.300000) rotate(-90.000000) translate(-16.000000, -16.300000) "
                    points="22.3 28.9 9.7 16.3 22.3 3.7"
                  />
                </g>
              </svg>
            </animated.div>
          )}
        </ArrowSpring>
      </PanelHeader>
      <ListSpring native state={expanded ? 'open' : 'close'}>
        {(styles: any): React.ReactNode => {
          return (
            <PanelBody style={styles}>
              <div>{children}</div>
            </PanelBody>
          );
        }}
      </ListSpring>
    </PanelRoot>
  );
};

export default ExpansionPanel;
